import React, { useState, useEffect } from 'react';
import { inspectionsAPI, reportsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { cn, formatDate, getStatusColor, getRecordTypeLabel, getInspectionTypeLabel, downloadBlob } from '../lib/utils';
import {
  FileBarChart,
  Download,
  Filter,
  Loader2,
  Calendar as CalendarIcon,
  FileSpreadsheet,
  FileText,
  ClipboardCheck,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function ReportsPage() {
  const { user, isAdmin } = useAuth();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Existing Filters
  const [status, setStatus] = useState('all');
  const [recordType, setRecordType] = useState('all');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  
  // New Filters
  const [inspectionStatus, setInspectionStatus] = useState('all');
  const [inspectionType, setInspectionType] = useState('all');
  const [ngvSystemOrigin, setNgvSystemOrigin] = useState('all');
  const [region, setRegion] = useState('all');

  useEffect(() => {
    loadData();
  }, [status, recordType, dateRange, inspectionStatus, inspectionType, ngvSystemOrigin, region]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (recordType !== 'all') params.record_type = recordType;
      if (inspectionStatus !== 'all') params.inspection_status = inspectionStatus;
      if (inspectionType !== 'all') params.inspection_type = inspectionType;
      if (ngvSystemOrigin !== 'all') params.ngv_system_origin = ngvSystemOrigin;
      if (region !== 'all') params.region = region;
      if (dateRange.from) {
        params.start_date = dateRange.from.toISOString();
        if (dateRange.to) params.end_date = dateRange.to.toISOString();
      }

      const response = await reportsAPI.exportInspections({ ...params, format: 'json' });
      setInspections(response.data);
    } catch (err) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const params = { format: 'pdf' };
      if (status !== 'all') params.status = status;
      if (recordType !== 'all') params.record_type = recordType;
      if (inspectionStatus !== 'all') params.inspection_status = inspectionStatus;
      if (inspectionType !== 'all') params.inspection_type = inspectionType;
      if (ngvSystemOrigin !== 'all') params.ngv_system_origin = ngvSystemOrigin;
      if (region !== 'all') params.region = region;
      if (dateRange.from) {
        params.start_date = dateRange.from.toISOString();
        if (dateRange.to) params.end_date = dateRange.to.toISOString();
      }

      const response = await reportsAPI.exportInspectionsPDF(params);
      downloadBlob(response.data, `Inspections_report_${format(new Date(), 'yyyy-MM-dd_HHmmss')}.pdf`);
      toast.success('PDF report exported successfully');
    } catch (err) {
      toast.error('Failed to export PDF report');
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = { format: 'excel' };
      if (status !== 'all') params.status = status;
      if (recordType !== 'all') params.record_type = recordType;
      if (inspectionStatus !== 'all') params.inspection_status = inspectionStatus;
      if (inspectionType !== 'all') params.inspection_type = inspectionType;
      if (ngvSystemOrigin !== 'all') params.ngv_system_origin = ngvSystemOrigin;
      if (region !== 'all') params.region = region;
      if (dateRange.from) {
        params.start_date = dateRange.from.toISOString();
        if (dateRange.to) params.end_date = dateRange.to.toISOString();
      }

      const response = await reportsAPI.exportInspectionsExcel(params);
      downloadBlob(response.data, `Inspections_report_${format(new Date(), 'yyyy-MM-dd_HHmmss')}.xlsx`);
      toast.success('Excel report exported successfully');
    } catch (err) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const clearAllFilters = () => {
    setStatus('all');
    setRecordType('all');
    setInspectionStatus('all');
    setInspectionType('all');
    setNgvSystemOrigin('all');
    setRegion('all');
    setDateRange({ from: null, to: null });
  };

  const hasActiveFilters = status !== 'all' || recordType !== 'all' || inspectionStatus !== 'all' || 
    inspectionType !== 'all' || ngvSystemOrigin !== 'all' || region !== 'all' || dateRange.from;

  const getIdentifier = (inspection) => {
    return inspection.registration_number || inspection.storage_serial_number || 'N/A';
  };

  // Summary stats
  const stats = {
    total: inspections.length,
    valid: inspections.filter(i => i.status === 'valid').length,
    expired: inspections.filter(i => i.status === 'expired').length,
    failed: inspections.filter(i => i.status === 'failed').length,
    pending: inspections.filter(i => i.status === 'pending' || i.status === 'conditionally_approved').length,
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Reports</h1>
          <p className="text-muted-foreground mt-1">
            Generate and export inspection reports
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExportPDF} variant="outline" disabled={exporting || inspections.length === 0}>
            {exporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileText className="mr-2 h-4 w-4" />
            )}
            Export PDF
          </Button>
          <Button onClick={handleExportExcel} disabled={exporting || inspections.length === 0}>
            {exporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="mr-2 h-4 w-4" />
            )}
            Export Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-2">
                {[status !== 'all', recordType !== 'all', inspectionStatus !== 'all', 
                  inspectionType !== 'all', ngvSystemOrigin !== 'all', region !== 'all', dateRange.from]
                  .filter(Boolean).length} active
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Row 1: Status filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Select value={inspectionStatus} onValueChange={setInspectionStatus}>
              <SelectTrigger data-testid="inspection-status-filter">
                <SelectValue placeholder="Inspection Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Inspection Status</SelectItem>
                <SelectItem value="valid">✅ Valid</SelectItem>
                <SelectItem value="expired">⏰ Expired</SelectItem>
                <SelectItem value="failed">❌ Failed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={inspectionType} onValueChange={setInspectionType}>
              <SelectTrigger data-testid="inspection-type-filter">
                <SelectValue placeholder="Inspection Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Inspection Types</SelectItem>
                <SelectItem value="new">🆕 New Installation</SelectItem>
                <SelectItem value="periodic">🔄 Periodic</SelectItem>
                <SelectItem value="after_accident">🔧 After Accident/Repair</SelectItem>
              </SelectContent>
            </Select>

            <Select value={ngvSystemOrigin} onValueChange={setNgvSystemOrigin}>
              <SelectTrigger data-testid="ngv-origin-filter">
                <SelectValue placeholder="NGV System Origin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All NGV Origins</SelectItem>
                <SelectItem value="OEM">🏭 OEM</SelectItem>
                <SelectItem value="Retrofitted">🔧 Retrofitted</SelectItem>
              </SelectContent>
            </Select>

            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger data-testid="region-filter">
                <SelectValue placeholder="Region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                <SelectItem value="Dar es Salaam">📍 Dar es Salaam</SelectItem>
                <SelectItem value="Morogoro">📍 Morogoro</SelectItem>
                <SelectItem value="Dodoma">📍 Dodoma</SelectItem>
                <SelectItem value="Mtwara">📍 Mtwara</SelectItem>
                <SelectItem value="others">📍 Others</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Row 2: Record type and date range */}
          <div className="flex flex-col md:flex-row gap-3">
            <Select value={recordType} onValueChange={setRecordType}>
              <SelectTrigger className="w-full md:w-48" data-testid="record-type-filter">
                <SelectValue placeholder="Record Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Record Types</SelectItem>
                <SelectItem value="car">🚗 Light Vehicle</SelectItem>
                <SelectItem value="3w">🛺 3-Wheeler</SelectItem>
                <SelectItem value="bus">🚌 Bus</SelectItem>
                <SelectItem value="truck">🛻 Truck</SelectItem>
                <SelectItem value="trailer">🚛 Trailer</SelectItem>
                <SelectItem value="cng_storage">🏭 CNG Storage</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn(
                  "w-full md:w-[280px] justify-start text-left font-normal",
                  !dateRange.from && "text-muted-foreground"
                )}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "MMM d, yyyy")} - {format(dateRange.to, "MMM d, yyyy")}
                      </>
                    ) : (
                      format(dateRange.from, "MMM d, yyyy")
                    )
                  ) : (
                    "Select date range"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => setDateRange(range || { from: null, to: null })}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>

            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                onClick={clearAllFilters}
                className="text-destructive hover:text-destructive"
              >
                <X className="mr-2 h-4 w-4" />
                Clear All Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <ClipboardCheck className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <span className="text-lg font-bold text-emerald-600">{stats.valid}</span>
              </div>
              <div>
                <p className="text-sm font-medium">Valid</p>
                <p className="text-xs text-muted-foreground">
                  {stats.total > 0 ? Math.round((stats.valid / stats.total) * 100) : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                <span className="text-lg font-bold text-red-600">{stats.expired}</span>
              </div>
              <div>
                <p className="text-sm font-medium">Expired</p>
                <p className="text-xs text-muted-foreground">
                  {stats.total > 0 ? Math.round((stats.expired / stats.total) * 100) : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-400">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
                <span className="text-lg font-bold text-red-500">{stats.failed}</span>
              </div>
              <div>
                <p className="text-sm font-medium">Failed</p>
                <p className="text-xs text-muted-foreground">
                  {stats.total > 0 ? Math.round((stats.failed / stats.total) * 100) : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <span className="text-lg font-bold text-amber-600">{stats.pending}</span>
              </div>
              <div>
                <p className="text-sm font-medium">Pending</p>
                <p className="text-xs text-muted-foreground">
                  {stats.total > 0 ? Math.round((stats.pending / stats.total) * 100) : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileBarChart className="h-5 w-5 text-primary" />
            Inspection Data
          </CardTitle>
          <CardDescription>
            {inspections.length} records matching your filters
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : inspections.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-medium text-lg mb-1">No data found</h3>
              <p className="text-muted-foreground text-sm">
                Try adjusting your filters to see more results
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[500px]">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead>Verification #</TableHead>
                    <TableHead>Identifier</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Record Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Inspector</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>Valid From</TableHead>
                    <TableHead>Valid To</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inspections.map((inspection) => (
                    <TableRow key={inspection.id}>
                      <TableCell className="font-mono text-xs">
                        {inspection.verification_number}
                      </TableCell>
                      <TableCell className="font-mono font-medium">
                        {getIdentifier(inspection)}
                      </TableCell>
                      <TableCell className="capitalize">
                        {getInspectionTypeLabel(inspection.inspection_type)}
                      </TableCell>
                      <TableCell>
                        {getRecordTypeLabel(inspection.record_type)}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline"
                          className={cn("capitalize", getStatusColor(inspection.status))}
                        >
                          {inspection.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{inspection.inspector_name || '-'}</TableCell>
                      <TableCell>{inspection.region || '-'}</TableCell>
                      <TableCell>{inspection.valid_from ? formatDate(inspection.valid_from) : '-'}</TableCell>
                      <TableCell>{inspection.valid_to ? formatDate(inspection.valid_to) : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
